/**
 * Gráfico horizontal de barras por cliente (Utilidad año1 vs año2).
 * Ordenado de mayor a menor utilidad año2.
 * SVG puro, sin librerías externas.
 */
import { ClientYearComparisonRowDto } from '../../../../../shared/services/results/types.comparativos';
import { money } from './comparativosUtils';

interface Props {
  rows: ClientYearComparisonRowDto[];
  anio1: number;
  anio2: number;
  topN?: number;
}

const COLOR_ANIO1 = '#94a3b8'; // slate-400
const COLOR_ANIO2 = '#3b82f6'; // blue-500
const COLOR_NEG   = '#ef4444'; // red-500

export function ClientsYearChart({ rows, anio1, anio2, topN = 12 }: Props) {
  const sorted = [...rows]
    .sort((a, b) => b.utilidadAnio2 - a.utilidadAnio2)
    .slice(0, topN);

  if (sorted.length === 0) return null;

  const BAR_H = 18;
  const GAP   = 6;
  const ROW_H = BAR_H * 2 + GAP + 12; // 2 barras + separación + label
  const PAD   = { top: 24, right: 120, bottom: 20, left: 160 };
  const W     = 680;
  const chartH = sorted.length * ROW_H;
  const H      = chartH + PAD.top + PAD.bottom;

  const maxVal = Math.max(
    ...sorted.flatMap((r) => [Math.abs(r.utilidadAnio1), Math.abs(r.utilidadAnio2)]),
    1,
  );
  const chartW = W - PAD.left - PAD.right;
  const bw = (v: number) => (Math.abs(v) / maxVal) * chartW;

  return (
    <div>
      <p className="mb-2 text-sm font-semibold text-ink-700">Utilidad por cliente — {anio1} vs {anio2}</p>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" aria-label="Comparativo clientes año vs año">
        {/* Eje vertical */}
        <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={PAD.top + chartH} stroke="#cbd5e1" strokeWidth={1} />

        {sorted.map((r, i) => {
          const y0 = PAD.top + i * ROW_H;
          const u1 = r.utilidadAnio1;
          const u2 = r.utilidadAnio2;
          const c2 = u2 >= 0 ? COLOR_ANIO2 : COLOR_NEG;

          return (
            <g key={r.cliente}>
              {/* Nombre cliente */}
              <text
                x={PAD.left - 6}
                y={y0 + BAR_H * 1.5}
                textAnchor="end"
                fontSize={9}
                fill="#475569"
                className="truncate"
              >
                {r.cliente.length > 22 ? r.cliente.slice(0, 20) + '…' : r.cliente}
              </text>

              {/* Barra año1 */}
              <rect x={PAD.left} y={y0 + 1} width={bw(u1)} height={BAR_H - 2} fill={COLOR_ANIO1} rx={2} opacity={0.85}>
                <title>{anio1}: {money(u1)}</title>
              </rect>

              {/* Barra año2 */}
              <rect x={PAD.left} y={y0 + BAR_H + GAP} width={bw(u2)} height={BAR_H - 2} fill={c2} rx={2}>
                <title>{anio2}: {money(u2)}</title>
              </rect>

              {/* Valor año2 al final */}
              <text
                x={PAD.left + bw(u2) + 4}
                y={y0 + BAR_H + GAP + BAR_H - 4}
                fontSize={8}
                fill={u2 >= 0 ? '#1e40af' : '#b91c1c'}
                fontWeight={600}
              >
                {money(u2)}
              </text>

              {/* Separador filas */}
              {i < sorted.length - 1 && (
                <line
                  x1={0}
                  y1={y0 + ROW_H - 2}
                  x2={W}
                  y2={y0 + ROW_H - 2}
                  stroke="#f1f5f9"
                  strokeWidth={1}
                />
              )}
            </g>
          );
        })}

        {/* Leyenda */}
        <rect x={PAD.left} y={H - 14} width={10} height={8} fill={COLOR_ANIO1} rx={1} />
        <text x={PAD.left + 14} y={H - 7} fontSize={9} fill="#64748b">{anio1}</text>
        <rect x={PAD.left + 50} y={H - 14} width={10} height={8} fill={COLOR_ANIO2} rx={1} />
        <text x={PAD.left + 64} y={H - 7} fontSize={9} fill="#64748b">{anio2}</text>
        <rect x={PAD.left + 100} y={H - 14} width={10} height={8} fill={COLOR_NEG} rx={1} />
        <text x={PAD.left + 114} y={H - 7} fontSize={9} fill="#64748b">{anio2} (negativo)</text>
      </svg>
    </div>
  );
}
